import { createActions, createReducer } from "reduxsauce";
import { has, get, every, initial, reject } from "lodash";
import Immutable from "seamless-immutable";

export const { Types, Creators } = createActions({
  selectUserInQueue: ["registration"],
  groupRegistration: ["location", "registrations"],
  groupRegistrationSucceeded: [],
  groupRegistrationFailed: [],
  unGroupRegistration: ["location", "registration"],
  unGroupRegistrationSucceeded: [],
  unGroupRegistrationFailed: [],
  assignUserToLane: ["location", "range", "laneNumber", "selectedUser"],
  assignUserToLaneSucceeded: [],
  assignUserToLaneFailed: [],
  updateQueueOrder: ["location", "queueUsers"],
  updateQueueOrderSucceeded: [],
  updateQueueOrderFailed: [],
  openLaneDetail: ["location", "range", "laneNumber", "lane"],
  changeLane: ["location", "range", "laneNumber", "updateLaneObject"],
  backToQueue: ["location", "range", "laneNumber"],
  completeEditLane: [],

  openQueueItemDetail: ["location", "registration"],
  updateQueueItem: ["location", "registrationId", "updateData"],
  deleteQueueItem: ["location", "registrationId"],
  completeEditQueueItem: [],

  updateQuickAddDrawerVisibility: ["visibility"],
  quickAddCustomer: ["location", "registration"],
  addCustomerToQuickAdd: ["customer"],
  removeCustomerFromQuickAdd: ["customerKey"],
  synchronizeCustomers: ["customers"],
  setSyncStatus: ["isSynchronizing", "isSynchronized"],
  addCustomerToSync: ["customer"],
  syncComplete: ["customers"],
  clearQuickAdd: [],

  //TODO: move actionSucceeded to common module
  // A place holder action to call
  // when redux-observable successfully finish the input action (SAVE, DELETE, ...)
  actionSucceeded: [],
});

/*
Initial State
 */
const initialState = Immutable({
  selectedUserInQueue: null,
  editLane: null,
  // control show/hide QuickAdd drawer
  quickAddDrawerVisibility: false,
  synchronizingCustomers: false,
  isSynchronized: false,
  customersToQuickAdd: [],
});

/*
Reducer
 */
const onSelectUserInQueue = (state = initialState, action) =>
  Immutable.merge(state, {
    //store reference only so we always can fetch latest data of selected queue item
    selectedUserInQueue: action.registration
      ? action.registration.registrationId
      : null,
  });

const onAssignUserToLaneSucceeded = (state = initialState) =>
  Immutable.merge(state, {
    selectedUserInQueue: null,
  });
const onGroupRegistrationSucceeded = (state = initialState) =>
  Immutable.merge(state, {
    selectedUserInQueue: null,
  });
const onGroupRegistrationFailed = (state = initialState) =>
  Immutable.merge(state, {
    selectedUserInQueue: null,
  });

const onUnGroupRegistrationSucceeded = (state = initialState) =>
  Immutable.merge(state, {
    selectedUserInQueue: null,
  });
const onUnGroupRegistrationFailed = (state = initialState) =>
  Immutable.merge(state, {
    selectedUserInQueue: null,
  });

const onOpenLaneDetail = (state = initialState, action) =>
  Immutable.merge(state, {
    editLane: {
      location: action.location,
      range: action.range,
      laneNumber: action.laneNumber,
      lane: action.lane,
    },
  });
const onCompleteEditLane = (state = initialState) =>
  Immutable.merge(state, {
    editLane: null,
  });

const onOpenQueueItemDetail = (state = initialState, action) =>
  Immutable.merge(state, {
    editQueueItem: action.registration,
  });
const onCompleteEditQueueItem = (state = initialState) =>
  Immutable.merge(state, {
    editQueueItem: null,
  });
const onUpdateQuickAddDrawerVisibility = (state = initialState, action) =>
  Immutable.merge(state, {
    quickAddDrawerVisibility: action.visibility,
  });

const onSynchronizeCustomers = (state = initialState, action) =>
  Immutable.merge(state, {
    synchronizingCustomers: true,
  });

const onSyncComplete = (state = initial, action) => {
  const syncedCustomers = action.customers.map((c) => {
    return { ...c, synchronized: true };
  });

  return Immutable.merge(state, {
    customersToQuickAdd: syncedCustomers,
    synchronizingCustomers: false,
  });
};

const onSetSyncStatus = (state = initialState, action) =>
  Immutable.merge(state, {
    synchronizingCustomers: action.isSynchronizing,
    isSynchronized: action.isSynchronized,
  });

const onAddCustomerToQuickAdd = (state = initialState, action) => {
  return Immutable.merge(state, {
    customersToQuickAdd: [...state.customersToQuickAdd, action.customer],
  });
};

const onRemoveCustomerFromQuickAdd = (state = initialState, action) => {
  return Immutable.merge(state, {
    customersToQuickAdd: reject(state.customersToQuickAdd, [
      "key",
      action.customerKey,
    ]),
  });
};

const onClearQuickAdd = (state = initialState, action) => {
  return Immutable.merge(state, {
    customersToQuickAdd: [],
  });
};

export default createReducer(initialState, {
  [Types.SELECT_USER_IN_QUEUE]: onSelectUserInQueue,
  [Types.GROUP_REGISTRATION_SUCCEEDED]: onGroupRegistrationSucceeded,
  [Types.GROUP_REGISTRATION_FAILED]: onGroupRegistrationFailed,
  [Types.ASSIGN_USER_TO_LANE_SUCCEEDED]: onAssignUserToLaneSucceeded,
  [Types.OPEN_LANE_DETAIL]: onOpenLaneDetail,
  [Types.COMPLETE_EDIT_LANE]: onCompleteEditLane,
  [Types.OPEN_QUEUE_ITEM_DETAIL]: onOpenQueueItemDetail,
  [Types.COMPLETE_EDIT_QUEUE_ITEM]: onCompleteEditQueueItem,
  [Types.UPDATE_QUICK_ADD_DRAWER_VISIBILITY]: onUpdateQuickAddDrawerVisibility,
  [Types.SYNCHRONIZE_CUSTOMERS]: onSynchronizeCustomers,
  [Types.SET_SYNC_STATUS]: onSetSyncStatus,
  [Types.ADD_CUSTOMER_TO_QUICK_ADD]: onAddCustomerToQuickAdd,
  [Types.REMOVE_CUSTOMER_FROM_QUICK_ADD]: onRemoveCustomerFromQuickAdd,
  [Types.SYNC_COMPLETE]: onSyncComplete,
  [Types.CLEAR_QUICK_ADD]: onClearQuickAdd,
  [Types.UN_GROUP_REGISTRATION_SUCCEEDED]: onUnGroupRegistrationSucceeded,
  [Types.UN_GROUP_REGISTRATION_FAILED]: onUnGroupRegistrationFailed,
});

/*
Selectors
*/
export function getRange(state, rangeLocation) {
  return has(state, ["data", "ranges", rangeLocation])
    ? state.data.ranges[rangeLocation]
    : {};
}

export const getIsLoading = (state, rangeLocation) =>
  !has(state, ["data", "queues", rangeLocation]) ||
  !has(state, ["data", "ranges", rangeLocation]);

export function getQueueUsers(state, rangeLocation) {
  if (
    !has(state, ["data", "queues", rangeLocation]) ||
    !state.data.queues[rangeLocation]
  ) {
    return [];
  }

  return Object.keys(state.data.queues[rangeLocation]).map((registrationId) => {
    const registration = state.data.queues[rangeLocation][registrationId];
    return {
      ...registration,
      registrationId,
    };
  });
}

export const isUserSelectedInQueue = (state, registrationId) => {
  if (
    !has(state, ["staff", "selectedUserInQueue"]) ||
    !state.staff.selectedUserInQueue
  ) {
    return false;
  }

  return state.staff.selectedUserInQueue === registrationId;
};

export const getSelectedUserInQueue = (state) => {
  const users = getQueueUsers(state, state.login.location);
  const foundUsers = users.filter(
    (user) => user.registrationId === state.staff.selectedUserInQueue
  );
  if (foundUsers.length > 0) {
    return foundUsers[0];
  } else {
    return null;
  }
};

export const getEditLane = (state) => state.staff.editLane;

export const getEditQueueItem = (state) => {
  if (!has(state, ["staff", "editQueueItem"])) {
    return null;
  }
  return state.staff.editQueueItem;
};

export const getSearchCustomerByPhoneResult = (state) => {
  if (!has(state, ["data", "searchCustomerResult"])) {
    return [];
  }
  return state.data.searchCustomerResult;
};

export const getQuickAddDrawerVisibility = (state) =>
  get(state, "staff.quickAddDrawerVisibility");

export const getSynchronizingCustomers = (state) =>
  get(state, "staff.synchronizingCustomers");

export const getIsSynchronized = (state) => {
  const customers = get(state, "staff.customersToQuickAdd");
  return customers.length > 0 && every(customers, "synchronized");
};

export const getCustomersToQuickAdd = (state) =>
  get(state, "staff.customersToQuickAdd");



// WEBPACK FOOTER //
// ./src/staff/reducer.js